import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Send, Loader2, Upload, Users, FileVideo, Image as ImageIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadFile, buildFileUrl } from '../../lib/files';

const AUDIENCES = [
  { value: 'ALL', label: 'All Clients' },
  { value: 'SEGMENT', label: 'Segment…' },
  { value: 'USER', label: 'Specific Users' },
];
const SEGMENTS = [
  { value: 'BOARDED', label: 'Boarded with us' },
  { value: 'BOARD_AND_TRAIN', label: 'Board & Train customers' },
  { value: 'VIP', label: 'VIP Members' },
];

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [audience, setAudience] = useState('ALL');
  const [segment, setSegment] = useState('BOARDED');
  const [targetUsers, setTargetUsers] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get('/admin/gallery').then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get('/admin/clients').then(r => setClients(r.data || [])).catch(()=>{});
  }, []);

  const submit = async () => {
    if (!file) { toast.error('Choose a photo or video'); return; }
    setUploading(true);
    try {
      const uploaded = await uploadFile(file, 'dog_photo', api);
      const body = { file_id: uploaded.id, title: title || null, caption: caption || null, audience };
      if (audience === 'SEGMENT') body.segment = segment;
      if (audience === 'USER') {
        if (targetUsers.length === 0) { toast.error('Pick at least one user'); setUploading(false); return; }
        body.target_user_ids = targetUsers;
      }
      await api.post('/gallery/push', body);
      toast.success(audience === 'ALL' ? 'Sent to all clients' : audience === 'SEGMENT' ? `Sent to ${segment}` : `Sent to ${targetUsers.length} user(s)`);
      setFile(null); setTitle(''); setCaption(''); setTargetUsers([]);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Remove this media from all clients?')) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  const toggleUser = (id) => {
    setTargetUsers((arr) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  };

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto'>
      <div className='mb-5'>
        <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Marketing · Media</div>
        <h1 className='font-display text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>Gallery Push</h1>
        <p className='text-sm text-slate-500 dark:text-slate-400 mt-1'>Send photos and videos to one client, a segment, or every client at once.</p>
      </div>

      <div className='grid lg:grid-cols-[1fr_1fr] gap-4'>
        <Card className='p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900' data-testid='gallery-composer'>
          <h2 className='font-display font-bold text-lg text-slate-900 dark:text-slate-50 mb-3'>New media</h2>
          <div className='space-y-3'>
            <div>
              <Label>Photo or video *</Label>
              <input
                type='file'
                accept='image/*,video/mp4,video/webm,video/quicktime'
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && f.size > 75 * 1024 * 1024) { toast.error('Max 75 MB'); return; }
                  setFile(f || null);
                }}
                className='block mt-1.5 w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-slate-200 dark:file:border-slate-700 file:bg-slate-50 dark:file:bg-slate-800'
                data-testid='gallery-file-input'
              />
              {file && <div className='text-xs text-slate-500 mt-1'>{file.name} · {(file.size/1024/1024).toFixed(2)} MB</div>}
            </div>
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. Week 1 progress' data-testid='gallery-title' />
            </div>
            <div>
              <Label>Caption</Label>
              <Textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder='Optional note to clients…' data-testid='gallery-caption' />
            </div>
            <div>
              <Label>Send to</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className='mt-1.5' data-testid='gallery-audience'><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {audience === 'SEGMENT' && (
              <div>
                <Label>Segment</Label>
                <Select value={segment} onValueChange={setSegment}>
                  <SelectTrigger className='mt-1.5' data-testid='gallery-segment'><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEGMENTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {audience === 'USER' && (
              <div>
                <Label>Pick users ({targetUsers.length})</Label>
                <div className='mt-1.5 max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md p-2 space-y-1'>
                  {clients.slice(0, 200).map(c => (
                    <label key={c.id} className='flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer'>
                      <input type='checkbox' checked={targetUsers.includes(c.id)} onChange={() => toggleUser(c.id)} />
                      <span className='truncate'>{c.full_name} <span className='text-slate-400'>· {c.email}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={submit} disabled={!file || uploading} className='w-full bg-[#1B3A87] hover:bg-[#16306F] text-white' data-testid='gallery-send-button'>
              {uploading ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : <Send className='w-4 h-4 mr-2' />}
              {uploading ? 'Sending…' : 'Send media'}
            </Button>
          </div>
        </Card>

        <Card className='p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
          <h2 className='font-display font-bold text-lg text-slate-900 dark:text-slate-50 mb-3'>Recent pushes</h2>
          <div className='space-y-3 max-h-[70vh] overflow-y-auto'>
            {items.length === 0 ? (
              <div className='text-sm text-slate-500'>No pushes yet.</div>
            ) : items.map(it => {
              const url = buildFileUrl(it.file.url || it.file.id);
              const isVideo = it.file.content_type?.startsWith('video/');
              return (
                <div key={it.id} className='flex gap-3 p-3 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700' data-testid={`admin-gallery-item-${it.id}`}>
                  <div className='w-24 h-24 shrink-0 rounded overflow-hidden bg-black'>
                    {isVideo ? <video src={url} muted preload='metadata' className='w-full h-full object-cover' /> : <img src={url} alt='' className='w-full h-full object-cover' />}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <Badge className='bg-[#1B3A87]/15 text-[#1B3A87] dark:text-[#93C5FD] border-0 text-[10px]'>
                        {isVideo ? <FileVideo className='w-3 h-3 mr-1' /> : <ImageIcon className='w-3 h-3 mr-1' />}
                        {it.audience}{it.segment ? ` · ${it.segment}` : ''}
                      </Badge>
                      <Badge variant='outline' className='text-[10px] border-slate-300 dark:border-slate-600'>
                        <Users className='w-3 h-3 mr-1' /> {it.target_user_ids?.length || 0}
                      </Badge>
                    </div>
                    {it.title && <div className='font-semibold text-sm text-slate-900 dark:text-slate-50 mt-1 truncate'>{it.title}</div>}
                    <div className='text-xs text-slate-500 mt-0.5'>{new Date(it.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={() => removeItem(it.id)} className='text-slate-400 hover:text-[#B42318] self-start' data-testid={`admin-gallery-delete-${it.id}`}><Trash2 className='w-4 h-4' /></button>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
